import type { BenchmarkBucket } from "./budgetBucketClassification";
import type { BenchmarkSource } from "./budgetBenchmarks";
import type { ProjectTypeKey } from "store/types/project";
import type { BenchmarkSet } from "./budgetBenchmarks";

export interface BenchmarkBucketDefinition {
  bucket: BenchmarkBucket;
  label: string;
  description: string;
}

export interface BenchmarkMethodology {
  summary: string;
  how_it_works: string;
  privacy_notice: string;
  industry_source: string;
  peer_source: string;
}

export interface BenchmarkProductionTypeDefinition {
  key: ProjectTypeKey;
  label: string;
  description: string;
}

export interface BudgetBenchmarkMeta {
  version: number;
  updated_at: string;
  buckets: BenchmarkBucketDefinition[];
  methodology: BenchmarkMethodology;
  production_types: BenchmarkProductionTypeDefinition[];
  min_peer_sample_size: number;
}

export const BENCHMARK_METHODOLOGY_COPY = {
  summary:
    "Every dollar in your budget, measured against how real productions of your type actually allocate theirs. It is a fast, credible reality check you can walk into any financier, studio, or investor conversation with.",
  how_it_works:
    "We sort your line items into the four buckets every budget shares: Above the Line, Production, Post, and Other. For each one, we compare your share of the total against the range we see across real productions like yours. Land inside the range and you are right where comparable productions sit. Land outside it and we flag it, so you can decide whether it is a deliberate choice or worth a second look. This is never pass or fail. It is the same gut check a seasoned line producer would give you, backed by data instead of a hunch.",
  privacy_notice:
    "We never read your script, your schedule, or the line-level detail of your budget. For benchmarks we only use four anonymous numbers: the percentage your total puts toward Above the Line, Production, Post, and Other. That is enough to show you how your budget shape compares to similar productions, and when you contribute, it helps others get the same perspective. Nothing else from your budget is shared.",
  industry_source:
    "Industry ranges are curated by our team from published studio and independent budget breakdowns, guild and union rate cards, and real budgets we have reviewed across feature, television, documentary, and commercial work. We revise them as the market moves, not once and forgotten.",
  peer_source:
    "Peer ranges come from real budgets built in RivetAI for the same kind of production. We only surface them once enough budgets are in the pool to be statistically meaningful, and every number is an anonymous aggregate, never a single project.",
} as const;

export const FALLBACK_BENCHMARK_META: BudgetBenchmarkMeta = {
  version: 1,
  updated_at: new Date(0).toISOString(),
  buckets: [
    {
      bucket: "atl",
      label: "ATL",
      description:
        "Above-the-line creative costs: story and rights, producers, director, and principal cast.",
    },
    {
      bucket: "production",
      label: "Production",
      description:
        "Below-the-line physical production: crew, equipment, locations, art, transportation, and catering.",
    },
    {
      bucket: "post",
      label: "Post",
      description:
        "Editorial, VFX, sound, music, color, and deliverables after principal photography.",
    },
    {
      bucket: "other",
      label: "Other",
      description:
        "Insurance, completion bond, contingency, fringes treated as overhead, and similar wrap costs.",
    },
  ],
  methodology: {
    summary: BENCHMARK_METHODOLOGY_COPY.summary,
    how_it_works: BENCHMARK_METHODOLOGY_COPY.how_it_works,
    privacy_notice: BENCHMARK_METHODOLOGY_COPY.privacy_notice,
    industry_source: BENCHMARK_METHODOLOGY_COPY.industry_source,
    peer_source: BENCHMARK_METHODOLOGY_COPY.peer_source,
  },
  production_types: [
    {
      key: "feature",
      label: "Feature",
      description: "Narrative feature films; balanced ATL and production with meaningful post.",
    },
    {
      key: "studio",
      label: "Studio feature",
      description: "Higher-budget studio features with stronger ATL and tighter production bands.",
    },
    {
      key: "short",
      label: "Short film",
      description: "Short-form projects with lower ATL share and heavier production concentration.",
    },
    {
      key: "documentary",
      label: "Documentary",
      description: "Non-fiction projects with lower cast share and higher post/editorial weight.",
    },
    {
      key: "commercial",
      label: "Commercial",
      description: "Commercial and branded content shaped closer to AICP-style allocations.",
    },
    {
      key: "other",
      label: "Other",
      description: "General production fallback when the project type is unspecified.",
    },
  ],
  min_peer_sample_size: 5,
};

export function formatBenchmarkRangeLabel(
  bucket: BenchmarkBucket,
  benchmarkSet: BenchmarkSet
): string {
  const range = benchmarkSet[bucket];
  return `${range.min}–${range.max}% (typical ${range.typical}%)`;
}

export function formatBenchmarkSourceLabel(source: BenchmarkSource): string {
  return source === "peers" ? "RivetAI peer budgets" : "Industry benchmarks";
}

function coalesceMethodologyText(...values: Array<string | null | undefined>): string | undefined {
  for (const value of values) {
    if (typeof value === "string" && value.trim().length > 0) {
      return value.trim();
    }
  }
  return undefined;
}

interface LegacyBenchmarkMethodology {
  summary?: string;
  how_it_works?: string;
  privacy_notice?: string;
  classification?: string;
  verdict_rules?: string;
  industry_source?: string;
  peer_source?: string;
}

export type BudgetBenchmarkMetaInput = Partial<Omit<BudgetBenchmarkMeta, "methodology">> & {
  methodology?: LegacyBenchmarkMethodology;
};

export function normalizeBudgetBenchmarkMeta(meta: BudgetBenchmarkMetaInput): BudgetBenchmarkMeta {
  const fallbackMethodology = FALLBACK_BENCHMARK_META.methodology;
  const methodology: LegacyBenchmarkMethodology = meta.methodology ?? {};

  return {
    version: meta.version ?? FALLBACK_BENCHMARK_META.version,
    updated_at: meta.updated_at ?? FALLBACK_BENCHMARK_META.updated_at,
    buckets:
      meta.buckets && meta.buckets.length > 0 ? meta.buckets : FALLBACK_BENCHMARK_META.buckets,
    production_types:
      meta.production_types && meta.production_types.length > 0
        ? meta.production_types
        : FALLBACK_BENCHMARK_META.production_types,
    min_peer_sample_size: meta.min_peer_sample_size ?? FALLBACK_BENCHMARK_META.min_peer_sample_size,
    methodology: {
      summary:
        coalesceMethodologyText(methodology.summary, fallbackMethodology.summary) ??
        fallbackMethodology.summary,
      how_it_works:
        coalesceMethodologyText(
          methodology.how_it_works,
          methodology.verdict_rules,
          methodology.classification,
          fallbackMethodology.how_it_works
        ) ?? fallbackMethodology.how_it_works,
      privacy_notice:
        coalesceMethodologyText(methodology.privacy_notice, fallbackMethodology.privacy_notice) ??
        fallbackMethodology.privacy_notice,
      industry_source:
        coalesceMethodologyText(methodology.industry_source, fallbackMethodology.industry_source) ??
        fallbackMethodology.industry_source,
      peer_source:
        coalesceMethodologyText(methodology.peer_source, fallbackMethodology.peer_source) ??
        fallbackMethodology.peer_source,
    },
  };
}

export function resolveBudgetBenchmarkMeta(
  meta: BudgetBenchmarkMetaInput | BudgetBenchmarkMeta | null | undefined
): BudgetBenchmarkMeta {
  if (!meta) {
    return FALLBACK_BENCHMARK_META;
  }
  return normalizeBudgetBenchmarkMeta(meta);
}
